<script>
	import { nanoid } from "nanoid";
	import { onMount, createEventDispatcher } from "svelte";

	import Month from "./Month.svelte";
	import DatePickerNavBar from "./DatePickerNavBar.svelte";
	import Popover from "./Popover.svelte";
	import { getMonths, areDatesEquivalent } from "./lib/helpers";
	import { keyCodes, keyCodesArray } from "./lib/keyCodes";

	const dispatch = createEventDispatcher();
	const today = new Date();
	let popover;

	export let format = "en-GB";
	export let label = null;
	export let sublabel = null;
	export let selected = null;
	export let formattedSelected;
	if (formattedSelected && !isFormattedDate(formattedSelected)) {
		formattedSelected = null;
	}
	if (formattedSelected && selected === null) {
		selected = unformatDate(formattedSelected, format);
	}

	export let openOnNodeType;
	export let openOnClass;
	export let start = new Date(1920, 1, 1);
	export let end = new Date(2100, 1, 1);
	export let placeholder = "e.g. 01/02/2014";
	export let dateChosen = false;
	export let trigger = null;
	export let selectableCallback = null;
	export let weekStart = 0;
	export let daysOfWeek = [
		["Sunday", "Sun"],
		["Monday", "Mon"],
		["Tuesday", "Tue"],
		["Wednesday", "Wed"],
		["Thursday", "Thu"],
		["Friday", "Fri"],
		["Saturday", "Sat"]
	];
	export let monthsOfYear = [
		["January", "Jan"],
		["February", "Feb"],
		["March", "Mar"],
		["April", "Apr"],
		["May", "May"],
		["June", "Jun"],
		["July", "Jul"],
		["August", "Aug"],
		["September", "Sep"],
		["October", "Oct"],
		["November", "Nov"],
		["December", "Dec"]
	];

	export let style = "";

	// theming variables:
	export let buttonBackgroundColor = "#fff";
	export let buttonBorderColor = "#eee";
	export let buttonTextColor = "#333";
	export let highlightColor = "#f7901e";
	export let dayBackgroundColor = "none";
	export let dayTextColor = "#4a4a4a";
	export let dayHighlightedBackgroundColor = "#efefef";
	export let dayHighlightedTextColor = "#4a4a4a";
	export let defaultToday = false;
	export let defaultHighlighted = null;
	export let validator;
	export let validatorMessage;
	export let required;
	export let disabled;
	export let id = nanoid(6);

	//internationalize({ daysOfWeek, monthsOfYear });
	let sortedDaysOfWeek =
		weekStart === 0
			? daysOfWeek
			: (() => {
					let dow = daysOfWeek.slice();
					dow.push(dow.shift());
					return dow;
				})();

	let highlighted = today;
	let shouldShakeDate = false;
	let shakeHighlightTimeout;
	let month = today.getMonth();
	let year = today.getFullYear();

	let isOpen = false;
	let isClosing = false;
	let dateInput = false;

	today.setHours(0, 0, 0, 0);
	selected = new Date(selected) == "Invalid Date" ? (defaultToday ? today : null) : selected;

	function assignmentHandler(formatted) {
		if (!trigger) return;
		trigger.innerHTML = formatted;
	}

	$: months = getMonths(start, end, selectableCallback, weekStart);

	let monthIndex = 0;
	$: {
		monthIndex = 0;
		for (let i = 0; i < months.length; i += 1) {
			if (months[i].month === month && months[i].year === year) {
				monthIndex = i;
			}
		}
	}
	$: visibleMonth = months[monthIndex];

	$: visibleMonthId = year + month / 100;
	$: lastVisibleDate = visibleMonth.weeks[visibleMonth.weeks.length - 1].days[6].date;
	$: firstVisibleDate = visibleMonth.weeks[0].days[0].date;
	$: canIncrementMonth = monthIndex < months.length - 1;
	$: canDecrementMonth = monthIndex > 0;
	$: wrapperStyle = `
    --button-background-color: ${buttonBackgroundColor};
    --button-border-color: ${buttonBorderColor};
    --button-text-color: ${buttonTextColor};
    --highlight-color: ${highlightColor};
    --day-background-color: ${dayBackgroundColor};
    --day-text-color: ${dayTextColor};
    --day-highlighted-background-color: ${dayHighlightedBackgroundColor};
    --day-highlighted-text-color: ${dayHighlightedTextColor};
    ${style}
  `;

	function formatDate(date, format) {
		if (!date || typeof date !== "object" || !date.getFullYear || isNaN(date.getFullYear()) || !format) {
			return null;
		}

		let day = date.getDate();
		let month = date.getMonth() + 1;
		let year = date.getFullYear();

		if ((format || "en-GB") === "en-US") {
			return `${month < 10 ? "0" + month : "" + month}/${day < 10 ? "0" + day : "" + day}/${year}`;
		} else {
			return `${day < 10 ? "0" + day : "" + day}/${month < 10 ? "0" + month : "" + month}/${year}`;
		}

		return dateFormatter.format(date);
	}

	function isFormattedDate(formattedDate, format) {
		if (!formattedDate || typeof formattedDate === "object") {
			return false;
		}

		const components = formattedDate.split("/");

		if (components.length !== 3) {
			return false;
		}

		let date;
		let day;
		let month;
		if ((format || "en-GB") === "en-US") {
			date = new Date(components[2], components[0] - 1, components[1]);
			day = components[1];
			month = components[0] - 1;
		} else {
			date = new Date(components[2], components[1] - 1, components[0]);
			day = components[0];
			month = components[1] - 1;
		}

		return date.getMonth() == month && date.getDate() == day;
	}

	function unformatDate(formattedDate, format) {
		if (!formattedDate || typeof formattedDate === "object") return formattedDate;

		const components = formattedDate.split("/");

		if ((format || "en-GB") === "en-US") {
			return new Date(components[2], components[0] - 1, components[1]);
		} else {
			return new Date(components[2], components[1] - 1, components[0]);
		}
	}

	$: {
		if (selected) {
			formattedSelected = formatDate(selected, format);
		} else {
			formattedSelected = null;
		}
	}

	onMount(() => {
		let defaultDate = defaultHighlighted ? new Date(defaultHighlighted) : new Date();
		if (defaultDate > end) defaultDate = end;
		if (defaultDate < start) defaultDate = start;
		month = selected == null ? defaultDate.getMonth() : selected.getMonth();
		year = selected == null ? defaultDate.getFullYear() : selected.getFullYear();
	});

	function changeMonth(selectedMonth) {
		month = selectedMonth;
	}

	function changeYear(selectedYear) {
		year = selectedYear;
	}

	function incrementMonth(direction, date) {
		if (direction === 1 && !canIncrementMonth) return;
		if (direction === -1 && !canDecrementMonth) return;

		let current = new Date(year, month, 1);
		current.setMonth(current.getMonth() + direction);
		month = current.getMonth();
		year = current.getFullYear();
		highlighted = new Date(year, month, date || 1);
	}

	function incrementYear(direction) {
		if (direction === 1 && year == end.getFullYear()) return;
		if (direction === -1 && year == start.getFullYear()) return;

		let current = new Date(year, month, 1);
		current.setYear(current.getFullYear() + 12 * direction);
		month = current.getMonth();
		year = current.getFullYear();
		highlighted = new Date(year, month, 1);
	}

	function getDefaultHighlighted() {
		if (selected) return new Date(selected);
		if (defaultHighlighted) return new Date(defaultHighlighted);

		let defaultDate = new Date();
		if (defaultDate > end) defaultDate = end;
		if (defaultDate < start) defaultDate = start;

		return defaultDate;
	}

	function incrementDayHighlighted(amount) {
		highlighted = new Date(highlighted);
		highlighted.setDate(highlighted.getDate() + amount);
		if (amount > 0 && highlighted > lastVisibleDate) {
			return incrementMonth(1, highlighted.getDate());
		}
		if (amount < 0 && highlighted < firstVisibleDate) {
			return incrementMonth(-1, highlighted.getDate());
		}
		return highlighted;
	}

	function getDay(m, date) {
		for (let i = 0; i < m.weeks.length; i += 1) {
			for (let j = 0; j < m.weeks[i].days.length; j += 1) {
				if (areDatesEquivalent(m.weeks[i].days[j].date, date)) {
					return m.weeks[i].days[j];
				}
			}
		}
		return null;
	}

	function checkIfVisibleDateIsSelectable(date) {
		const day = getDay(visibleMonth, date);
		if (!day) return false;
		return day.selectable;
	}

	function shakeDate(date) {
		clearTimeout(shakeHighlightTimeout);
		shouldShakeDate = date;
		shakeHighlightTimeout = setTimeout(() => {
			shouldShakeDate = false;
		}, 700);
	}

	function assignValueToTrigger(formatted) {
		assignmentHandler(formatted);
	}

	function registerSelection(chosen) {
		if (!checkIfVisibleDateIsSelectable(chosen)) return shakeDate(chosen);
		// eslint-disable-next-line
		close();
		selected = chosen;
		dateChosen = true;
		assignValueToTrigger(formattedSelected);
		let formattedDate = formatDate(selected, format);

		if (validator) {
			//  The new value needs to be set immediately if it is being validated
			if (selected) {
				dateInput.value = formattedDate;
			}

			var evt = new Event("change");
			dateInput.dispatchEvent(evt);
		}

		return dispatch("dateSelected", { date: chosen, formattedDate: formattedDate });
	}

	function handleKeyPress(evt) {
		if (keyCodesArray.indexOf(evt.keyCode) === -1) return;
		evt.preventDefault();
		switch (evt.keyCode) {
			case keyCodes.left:
				incrementDayHighlighted(-1);
				break;
			case keyCodes.up:
				incrementDayHighlighted(-7);
				break;
			case keyCodes.right:
				incrementDayHighlighted(1);
				break;
			case keyCodes.down:
				incrementDayHighlighted(7);
				break;
			case keyCodes.pgup:
				incrementMonth(-1);
				break;
			case keyCodes.pgdown:
				incrementMonth(1);
				break;
			case keyCodes.escape:
				// eslint-disable-next-line
				close();
				break;
			case keyCodes.enter:
			case keyCodes.tab:
				registerSelection(highlighted);
				break;
			default:
				break;
		}
	}

	function registerClose() {
		document.removeEventListener("keydown", handleKeyPress);
		dispatch("close");
	}

	function close() {
		popover.close();
		registerClose();
	}

	function registerOpen() {
		highlighted = getDefaultHighlighted();
		month = selected == null ? highlighted.getMonth() : selected.getMonth();
		year = selected == null ? highlighted.getFullYear() : selected.getFullYear();
		document.addEventListener("keydown", handleKeyPress);
		dispatch("open");
	}

	function valueChanged(e) {
		if (e.target.value === "") {
			selected = null;
			return;
		}

		if (!isFormattedDate(e.target.value)) {
			// e.target.value = formattedSelected;
			e.target.value = null;

			// Moved error message into validation/index.js
			return;
		}

		let chosen = unformatDate(e.target.value);
		if (chosen < start || chosen > end) {
			//e.target.value = formattedSelected;
			e.target.value = null;

			// Moved error message into validation/index.js
		} else {
			selected = chosen;
			dispatch("dateChange", { target: e.target });

			let formattedDate = formatDate(selected, format);
			dispatch("dateSelected", { date: selected, formattedDate: formattedDate });
		}
	}
</script>

<div class="datepicker" class:open={isOpen} class:closing={isClosing} style={wrapperStyle}>
	<Popover
		bind:this={popover}
		bind:open={isOpen}
		bind:shrink={isClosing}
		{trigger}
		{openOnNodeType}
		{openOnClass}
		on:opened={registerOpen}
		on:closed={registerClose}
		{disabled}>
		<div slot="trigger">
			<slot>
				{#if !trigger}
					{#if label}
						<label for={id}>
							{label}

							{#if required}
								<span class="required">*</span>
							{/if}

							{#if sublabel}
								<div class="sublabel">{sublabel}</div>
							{/if}
						</label>
					{/if}
					<div class="date-group {id}">
						<span class="calendar-pop" class:calendarpop={!disabled} id="basic-addon1">
							<i class="calendar-icon" class:calendarpop={!disabled}></i>
						</span>
						<input
							{disabled}
							{id}
							bind:this={dateInput}
							{required}
							type="text"
							data-validation-type="date"
							data-date-start={formatDate(start, format)}
							data-date-end={formatDate(end, format)}
							data-validator={validator}
							data-bouncer-message={validatorMessage}
							class="form-control calendar-input"
							{placeholder}
							data-bouncer-target=".date-picker-error"
							aria-label="Date"
							on:change={(e) => valueChanged(e)}
							value={formattedSelected} />
						<div class="date-picker-error"></div>
					</div>
				{/if}
			</slot>
		</div>
		<div slot="contents">
			<div class="calendar">
				<DatePickerNavBar
					{month}
					{year}
					{start}
					{end}
					{canIncrementMonth}
					{canDecrementMonth}
					{monthsOfYear}
					on:monthSelected={(e) => changeMonth(e.detail)}
					on:yearSelected={(e) => changeYear(e.detail)}
					on:incrementMonth={(e) => incrementMonth(e.detail)}
					on:incrementYear={(e) => incrementYear(e.detail)} />
				<div class="legend">
					{#each sortedDaysOfWeek as day}
						<span>{day[1]}</span>
					{/each}
				</div>
				<Month
					bind:open={isOpen}
					{visibleMonth}
					{selected}
					{highlighted}
					{shouldShakeDate}
					id={visibleMonthId}
					on:dateSelected={(e) => registerSelection(e.detail)} />
			</div>
		</div>
	</Popover>
</div>

<style>
	.date-group {
		position: relative;
	}

	.date-group > .form-control:focus {
		z-index: auto;
	}
	/*
    .datepicker {
        display: inline-block;
        margin: 0 auto;
        overflow: visible;
        width: 100%;
    }
*/
	*,
	*:before,
	*:after {
		box-sizing: inherit;
	}

	.calendar {
		box-sizing: border-box;
		position: relative;
		overflow: hidden;
		user-select: none;
		width: 100vw;
		padding: 10px;
		padding-top: 0;
	}

	@media (min-width: 480px) {
		.calendar {
			height: auto;
			width: 340px;
			max-width: 100%;
		}
	}

	.legend {
		color: #4a4a4a;
		padding: 10px 0;
		margin-bottom: 5px;
	}

	.legend span {
		width: 14.285714%;
		display: inline-block;
		text-align: center;
	}

	.calendar-pop {
		cursor: default;
		position: absolute;
		right: 10px;
		top: 2px;
		line-height: 50px;
	}

	.calendar-pop.calendarpop {
		cursor: pointer;
	}

	.calendar-icon {
		display: inline-block;
		font: normal normal normal 14px/1 FontAwesome;
		font-size: inherit;
		text-rendering: auto;
		-webkit-font-smoothing: antialiased;
		-moz-osx-font-smoothing: grayscale;
	}

	.calendar-icon:before {
		content: "\f073";
	}

	.date-picker-error {
		width: 100%;
	}
</style>
